import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Calendar, Clock, Plus, Settings, Trash2, Check, X,
  ChevronLeft, ChevronRight, User, Briefcase
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WorkCalendar = () => {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [monthlyAvailability, setMonthlyAvailability] = useState(null);
  const [yearlyAvailability, setYearlyAvailability] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [showTimeOffModal, setShowTimeOffModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  
  const [holidayForm, setHolidayForm] = useState({
    name: '',
    date: '',
    is_recurring: false,
    hours_lost: 8
  });
  
  const [timeOffForm, setTimeOffForm] = useState({
    start_date: '',
    end_date: '',
    reason: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [currentYear, currentMonth]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const [monthlyRes, yearlyRes, holidaysRes, timeOffRes, settingsRes] = await Promise.all([
        axios.get(`${API}/work-calendar/availability/${currentYear}/${currentMonth}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/work-calendar/availability/${currentYear}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/work-calendar/holidays?year=${currentYear}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/work-calendar/time-off`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/work-calendar/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setMonthlyAvailability(monthlyRes.data);
      setYearlyAvailability(yearlyRes.data);
      setHolidays(holidaysRes.data.holidays || []);
      setTimeOffRequests(timeOffRes.data.requests || []);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to load calendar data:', error);
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const handlePrevMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleCreateHoliday = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...holidayForm,
        year: new Date(holidayForm.date).getFullYear()
      };
      
      await axios.post(`${API}/work-calendar/holidays`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Holiday added successfully!');
      setShowHolidayModal(false);
      setHolidayForm({ name: '', date: '', is_recurring: false, hours_lost: 8 });
      loadData();
    } catch (error) {
      console.error('Failed to create holiday:', error);
      toast.error('Failed to add holiday');
    }
  };

  const handleDeleteHoliday = async (id) => {
    if (!window.confirm('Delete this holiday?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/work-calendar/holidays/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Holiday deleted');
      // Reload all data to refresh the UI
      await loadData();
    } catch (error) {
      console.error('Failed to delete holiday:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete holiday');
    }
  };

  const handleCreateTimeOff = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/work-calendar/time-off`, timeOffForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Time off request submitted!');
      setShowTimeOffModal(false);
      setTimeOffForm({ start_date: '', end_date: '', reason: '', notes: '' });
      loadData();
    } catch (error) {
      console.error('Failed to create time off request:', error);
      toast.error('Failed to submit request');
    }
  };

  const handleApproveTimeOff = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/work-calendar/time-off/${id}/status?new_status=Approved`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Time off approved');
      loadData();
    } catch (error) {
      console.error('Failed to approve:', error);
      toast.error('Failed to approve request');
    }
  };

  const handleRejectTimeOff = async (id) => {
    const reason = window.prompt('Reason for rejection:');
    if (!reason) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/work-calendar/time-off/${id}/status?new_status=Rejected&rejection_reason=${encodeURIComponent(reason)}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request rejected');
      loadData();
    } catch (error) {
      console.error('Failed to reject:', error);
      toast.error('Failed to reject request');
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/work-calendar/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved!');
      setShowSettingsModal(false);
      loadData();
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    }
  };

  const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight uppercase text-gray-900">
            Work Calendar
          </h1>
          <p className="text-gray-700 mt-1 font-medium">
            Manage holidays, time off, and calculate available work hours
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowSettingsModal(true)}>
            <Settings size={16} className="mr-2" />
            Settings
          </Button>
        </div>
      </div>

      {/* Month Navigator */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={handlePrevMonth}>
              <ChevronLeft size={20} />
            </Button>
            <h2 className="text-2xl font-bold text-gray-900">
              {monthNames[currentMonth]} {currentYear}
            </h2>
            <Button variant="outline" onClick={handleNextMonth}>
              <ChevronRight size={20} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Summary */}
      {monthlyAvailability && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-2 border-blue-200 bg-blue-50">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-blue-600">
                {monthlyAvailability.working_days}
              </p>
              <p className="text-xs text-gray-700 mt-1">Working Days</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-red-200 bg-red-50">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-red-600">
                {monthlyAvailability.stat_holidays}
              </p>
              <p className="text-xs text-gray-700 mt-1">Stat Holidays</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-orange-200 bg-orange-50">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-orange-600">
                {monthlyAvailability.approved_time_off_days.toFixed(1)}
              </p>
              <p className="text-xs text-gray-700 mt-1">Time Off Days</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-green-200 bg-green-50">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-green-600">
                {monthlyAvailability.available_working_days.toFixed(1)}
              </p>
              <p className="text-xs text-gray-700 mt-1">Available Days</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-purple-200 bg-purple-50">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-purple-600">
                {monthlyAvailability.total_available_hours.toFixed(0)}
              </p>
              <p className="text-xs text-gray-700 mt-1">Available Hours</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="holidays">Stat Holidays</TabsTrigger>
          <TabsTrigger value="timeoff">Time Off</TabsTrigger>
          <TabsTrigger value="yearly">Yearly View</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {monthlyAvailability && (
            <Card>
              <CardHeader>
                <CardTitle className="text-gray-900">Hours Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="text-sm text-gray-700">Standard Working Hours:</span>
                  <span className="font-bold text-gray-900">
                    {monthlyAvailability.standard_working_hours.toFixed(0)} hrs
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-red-50 rounded">
                  <span className="text-sm text-gray-700">Holiday Hours Lost:</span>
                  <span className="font-bold text-red-600">
                    - {monthlyAvailability.holiday_hours_lost.toFixed(0)} hrs
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-orange-50 rounded">
                  <span className="text-sm text-gray-700">Time Off Hours Lost:</span>
                  <span className="font-bold text-orange-600">
                    - {monthlyAvailability.time_off_hours_lost.toFixed(0)} hrs
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded border-2 border-green-200">
                  <span className="text-base font-semibold text-gray-900">Total Available Hours:</span>
                  <span className="text-xl font-bold text-green-600">
                    {monthlyAvailability.total_available_hours.toFixed(0)} hrs
                  </span>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Holidays Tab */}
        <TabsContent value="holidays" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowHolidayModal(true)}>
              <Plus size={16} className="mr-2" />
              Add Holiday
            </Button>
          </div>
          
          {holidays.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-900 mb-2 font-semibold">No stat holidays defined</p>
                <p className="text-sm text-gray-700 mb-4">
                  Add statutory holidays to track non-working days
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {holidays.map((holiday) => (
                <Card key={holiday.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-900">{holiday.name}</p>
                        <p className="text-sm text-gray-600">
                          {new Date(holiday.date).toLocaleDateString()} • {holiday.hours_lost} hours
                          {holiday.is_recurring && <Badge variant="outline" className="ml-2">Recurring</Badge>}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteHoliday(holiday.id)}
                        className="text-red-500"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Time Off Tab */}
        <TabsContent value="timeoff" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowTimeOffModal(true)}>
              <Plus size={16} className="mr-2" />
              Request Time Off
            </Button>
          </div>

          {timeOffRequests.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Briefcase size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-900 mb-2 font-semibold">No time off requests</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {timeOffRequests.map((request) => (
                <Card key={request.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <User size={16} className="text-gray-600" />
                          <p className="font-semibold text-gray-900">{request.user_name}</p>
                          <Badge variant={
                            request.status === 'Approved' ? 'default' :
                            request.status === 'Rejected' ? 'destructive' : 'secondary'
                          }>
                            {request.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-700 mb-1">
                          <strong>Reason:</strong> {request.reason}
                        </p>
                        <p className="text-sm text-gray-600">
                          {new Date(request.start_date).toLocaleDateString()} to {new Date(request.end_date).toLocaleDateString()} 
                          • {request.total_days} days ({request.total_hours} hours)
                        </p>
                        {request.rejection_reason && (
                          <p className="text-sm text-red-600 mt-1">
                            <strong>Rejection Reason:</strong> {request.rejection_reason}
                          </p>
                        )}
                      </div>
                      {request.status === 'Pending' && (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleApproveTimeOff(request.id)}
                            className="text-green-500"
                          >
                            <Check size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRejectTimeOff(request.id)}
                            className="text-red-500"
                          >
                            <X size={16} />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Yearly View Tab */}
        <TabsContent value="yearly" className="space-y-4">
          {yearlyAvailability && (
            <>
              <Card className="border-2 border-purple-200 bg-purple-50">
                <CardContent className="pt-6 text-center">
                  <p className="text-5xl font-bold text-purple-600">
                    {yearlyAvailability.total_available_hours.toFixed(0)}
                  </p>
                  <p className="text-sm text-gray-700 mt-2">Total Available Hours for {currentYear}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-gray-900">Monthly Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {yearlyAvailability.monthly_breakdown.map((month) => (
                      <div key={month.month} className="p-3 bg-gray-50 rounded border border-gray-200">
                        <p className="text-sm font-semibold text-gray-900 mb-1">{month.month_name}</p>
                        <p className="text-xs text-gray-600">
                          Working Days: {month.available_working_days.toFixed(1)}
                        </p>
                        <p className="text-lg font-bold text-purple-600">
                          {month.total_available_hours.toFixed(0)} hrs
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Holiday Modal */}
      <Dialog open={showHolidayModal} onOpenChange={setShowHolidayModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-gray-900">Add Stat Holiday</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateHoliday} className="space-y-4">
            <div>
              <Label className="text-sm font-semibold text-gray-900">Holiday Name *</Label>
              <Input
                value={holidayForm.name}
                onChange={(e) => setHolidayForm({ ...holidayForm, name: e.target.value })}
                placeholder="e.g., Christmas Day"
                required
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-900">Date *</Label>
              <Input
                type="date"
                value={holidayForm.date}
                onChange={(e) => setHolidayForm({ ...holidayForm, date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-900">Hours Lost</Label>
              <Input
                type="number"
                step="0.5"
                value={holidayForm.hours_lost}
                onChange={(e) => setHolidayForm({ ...holidayForm, hours_lost: parseFloat(e.target.value) })}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="recurring"
                checked={holidayForm.is_recurring}
                onChange={(e) => setHolidayForm({ ...holidayForm, is_recurring: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="recurring" className="text-sm cursor-pointer">
                Recurring (repeats every year)
              </Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowHolidayModal(false)}>
                Cancel
              </Button>
              <Button type="submit">Add Holiday</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Time Off Request Modal */}
      <Dialog open={showTimeOffModal} onOpenChange={setShowTimeOffModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-gray-900">Request Time Off</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTimeOff} className="space-y-4">
            <div>
              <Label className="text-sm font-semibold text-gray-900">Start Date *</Label>
              <Input
                type="date"
                value={timeOffForm.start_date}
                onChange={(e) => setTimeOffForm({ ...timeOffForm, start_date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-900">End Date *</Label>
              <Input
                type="date"
                value={timeOffForm.end_date}
                onChange={(e) => setTimeOffForm({ ...timeOffForm, end_date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-900">Reason *</Label>
              <Input
                value={timeOffForm.reason}
                onChange={(e) => setTimeOffForm({ ...timeOffForm, reason: e.target.value })}
                placeholder="Vacation, Personal, etc."
                required
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-900">Notes</Label>
              <Textarea
                value={timeOffForm.notes}
                onChange={(e) => setTimeOffForm({ ...timeOffForm, notes: e.target.value })}
                placeholder="Additional details..."
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowTimeOffModal(false)}>
                Cancel
              </Button>
              <Button type="submit">Submit Request</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      {settings && (
        <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-gray-900">Calendar Settings</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveSettings} className="space-y-4">
              <div>
                <Label className="text-sm font-semibold text-gray-900">Default Hours Per Day</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={settings.default_hours_per_day}
                  onChange={(e) => setSettings({ ...settings, default_hours_per_day: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-gray-900">Working Days Per Week</Label>
                <Input
                  type="number"
                  value={settings.default_days_per_week}
                  onChange={(e) => setSettings({ ...settings, default_days_per_week: parseInt(e.target.value) })}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowSettingsModal(false)}>
                  Cancel
                </Button>
                <Button type="submit">Save Settings</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default WorkCalendar;
